import React, { useEffect, useMemo, useState } from "react";
import { ShieldAlert } from "lucide-react";
import toast from "react-hot-toast";

import { Card } from "@/components/ui/Card";
import Callout from "@/components/ui/Callout";
import SettingRow from "@/components/shared/SettingRow";
import { CenteredSpinner } from "@/components/shared/Loader";
import ModuleSelector from "@/pages/super-admin/tenant/components/ModuleSelector";
// CV Formatting is disabled — commented out, not deleted. See PLAN doc
// "resilient-stirring-tulip".
// import TemplateAccessPicker from "@/pages/super-admin/tenant/components/TemplateAccessPicker";
import { GUARDIAN_POLICIES, GUARDIAN_POLICY_LABELS, GUARDIAN_SWITCHES, MODULES } from "@/permissions/modules";
import { ModuleService } from "@/services/moduleService";
import { GuardianService } from "@/services/guardianService";
// import { ResumeTemplateAdminService } from "@/services/resumeTemplateAdminService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

// Sourcing, Extension, Interview Scheduling and CV Formatting are disabled
// on the frontend — commented out, not deleted, across the app (see PLAN
// doc "resilient-stirring-tulip"). Filtered out here too, at the one place
// this panel loads a tenant's module list, so none of the three ever shows
// as part of the tenant's plan.
const HIDDEN_MODULE_CODES = [MODULES.SOURCING, MODULES.EXTENSION, MODULES.INTERVIEW_SCHEDULING, MODULES.RESUME_TEMPLATE];

/**
 * One tenant's plan, **read-only**: which modules they are entitled to, plus
 * per-tenant guardian policy overrides.
 *
 * The module tree here shows and never changes. Editing a plan belongs behind
 * the Edit button on the tenant list, which is where a SuperAdmin looks for
 * it; a View screen that also saved was the surprise nobody wanted.
 *
 * Entitlement only. This panel never shows or writes fleet-wide availability
 * as an action — that lives on /super-admin/platform-settings. The two axes
 * stay separate so re-enabling a killed module needs no per-tenant action at
 * all; a module that happens to be dark right now still shows the tenant's
 * entitlement, with a note explaining the difference.
 *
 * **The guardian policy below is read-only too.** It used to be the one
 * editable control on this screen, which made View mean two things at once:
 * the modules beside it showed and never changed, and the policy saved on
 * change. Setting it lives on the Edit modal now, next to the module tree,
 * where every other decision about a tenant is made.
 */

const SWITCH_LABELS = {
  [GUARDIAN_SWITCHES.MASTER]: "Prompt-safety screening",
};

const GUARDIAN_SWITCH_KEYS = Object.values(GUARDIAN_SWITCHES);

const ModulesPanel = ({ tenantId }) => {
  const [modules, setModules] = useState([]);
  const [policies, setPolicies] = useState({});
  const [platformPolicies, setPlatformPolicies] = useState({});
  const [guardianAvailable, setGuardianAvailable] = useState(true);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [grantedTemplateKeys, setGrantedTemplateKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      // allSettled, not all: a failed template fetch must not blank out the
      // modules and policy this panel already has. Each source reports its
      // own failure independently.
      const [moduleResult, policyResult] = await Promise.allSettled([
        ModuleService.getTenantModules(tenantId),
        GuardianService.getTenantPolicy(tenantId),
        // CV Formatting is disabled — commented out, not deleted.
        // ResumeTemplateAdminService.fetchTenantTemplates(tenantId),
      ]);
      if (cancelled) return;

      if (moduleResult.status === "fulfilled") {
        setModules((moduleResult.value?.modules ?? []).filter((m) => !HIDDEN_MODULE_CODES.includes(m.code)));
      } else {
        toast.error(apiErrorMessageFormat(moduleResult.reason, "Failed to load tenant modules"));
      }

      if (policyResult.status === "fulfilled") {
        setPolicies(policyResult.value?.policies ?? {});
        setPlatformPolicies(policyResult.value?.platformPolicies ?? {});
        setGuardianAvailable(policyResult.value?.availableOnDeployment !== false);
      } else {
        toast.error(apiErrorMessageFormat(policyResult.reason, "Failed to load prompt-safety policy"));
      }

      // CV Formatting is disabled — commented out, not deleted.
      // if (templateResult.status === "fulfilled") {
      //   const list = templateResult.value?.data ?? [];
      //   setTemplateOptions(list.map((t) => ({ key: t.key, name: t.name, description: t.description })));
      //   setGrantedTemplateKeys(new Set(list.filter((t) => t.granted).map((t) => t.key)));
      // } else {
      //   // Non-fatal: the CV Formatting row still shows, just without its
      //   // granted-template list.
      //   setTemplateOptions([]);
      //   setGrantedTemplateKeys(new Set());
      // }

      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // `stored` drives the tick — what the tenant bought. A child of a
  // switched-off parent stays ticked and greys out, which is the honest
  // picture: they own it, and it waits for the parent.
  const entitled = useMemo(() => new Set(modules.filter((m) => m.stored).map((m) => m.code)), [modules]);

  if (loading) {
    return <CenteredSpinner className="h-40" iconClassName="h-5 w-5" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <div className="mb-4 flex flex-col gap-1.5">
          <h3 className="text-[15px] font-semibold text-text-primary">Modules in this tenant's plan</h3>
          <p className="max-w-3xl text-[13px] leading-relaxed text-text-secondary">
            {entitled.size} of {modules.length} enabled. Read-only here — change a plan from the Edit button on the tenant list. Changes take effect on the tenant's next
            request; nobody has to sign out and back in.
          </p>
        </div>

        {/* CV Formatting is disabled — commented out, not deleted.
        `renderExtra` is gated on `checked` as well as the module code:
            without it a tenant that never bought CV Formatting was still shown
            the template list, and told "No templates granted yet. Recruiters
            will see an empty list until at least one is ticked." about a
            feature they do not have. The edit form does the same — AddEditModel
            passes `locked={!checked}`.
        <ModuleSelector
          modules={modules}
          selected={entitled}
          onToggle={() => {}}
          readOnly
          renderExtra={(module, { checked }) =>
            module.code === MODULES.RESUME_TEMPLATE && checked ? <TemplateAccessPicker templates={templateOptions} selected={grantedTemplateKeys} readOnly /> : null
          }
        />
        */}
        <ModuleSelector modules={modules} selected={entitled} onToggle={() => {}} readOnly />
      </Card>

      <Card className="p-5">
        <div className="mb-4 flex flex-col gap-1.5">
          <h3 className="text-[15px] font-semibold text-text-primary">Prompt-safety policy for this tenant</h3>
          <p className="max-w-3xl text-[13px] leading-relaxed text-text-secondary">
            What is enforced for this tenant, and which level decided it. Read-only here — change it from the Edit button on the tenant list. A fleet-wide "Forced on" or
            "Forced off" wins outright; a per-tenant policy applies only while the platform policy is "Tenant decides".
          </p>
          {!guardianAvailable && (
            <Callout tone="neutral" icon={ShieldAlert} className="mt-1">
              No guardian model is configured on this deployment, so no screening runs regardless of these settings.
            </Callout>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {GUARDIAN_SWITCH_KEYS.map((switchKey) => {
            const stored = policies[switchKey] || GUARDIAN_POLICIES.TENANT_CHOICE;
            const platform = platformPolicies[switchKey] || GUARDIAN_POLICIES.TENANT_CHOICE;
            // The platform level wins, so what is *enforced* is not always what
            // this tenant's row says. Show the enforced value, and say which
            // level decided it — a panel that showed the stored row alone read
            // as the answer and was wrong whenever the platform forced.
            const platformForces = platform !== GUARDIAN_POLICIES.TENANT_CHOICE;
            const effective = platformForces ? platform : stored;
            return (
              <SettingRow
                key={switchKey}
                title={SWITCH_LABELS[switchKey]}
                className="flex-col items-stretch gap-3 p-4 sm:flex-row sm:items-center"
                contentClassName="gap-1"
                descriptionClassName="max-w-none text-[12px] leading-normal"
                // Hugs the pill. `sm:w-52` matched the width of the Select on
                // the Edit modal, but this row shows a read-only value, so the
                // fixed box left a strip of empty space to the right of the
                // pill that the left side of the row had no counterpart for.
                controlClassName="w-full sm:w-auto"
                description={
                  platformForces
                    ? `Set by the platform policy for every tenant. A per-tenant policy cannot override it.${
                        stored !== GUARDIAN_POLICIES.TENANT_CHOICE ? ` This tenant's stored policy (${GUARDIAN_POLICY_LABELS[stored]}) is kept for when it can.` : ""
                      }`
                    : stored === GUARDIAN_POLICIES.TENANT_CHOICE
                      ? "No override — the tenant decides, because the platform leaves the choice open."
                      : "Overriding the platform policy for this tenant."
                }
                control={
                  <span className="inline-flex rounded-md border border-border-primary/60 bg-background-primary/40 px-3 py-1.5 text-[13px] font-medium text-text-primary">
                    {GUARDIAN_POLICY_LABELS[effective] ?? effective}
                  </span>
                }
              />
            );
          })}
        </div>
      </Card>
    </div>
  );
};

export default ModulesPanel;
